import { useEffect, useRef } from 'react';
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import { Button } from "@/components/ui/button";
import { Link } from 'react-router-dom';

const CALENDLY_URL = "https://calendly.com/meneora/appel-clarte-de-15-minutes";

const Home = () => {
  const contactRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) entry.target.classList.add('revealed');
        });
      },
      { threshold: 0.1 }
    );
    const elements = document.querySelectorAll('.reveal-on-scroll');
    elements.forEach(el => observer.observe(el));

    if (window.location.hash === '#contact' && contactRef.current) {
      setTimeout(() => contactRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    }
    return () => elements.forEach(el => observer.unobserve(el));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <Hero />

      {/* SECTION 1.5 — INVITATION DOUCE (juste sous le hero) */}
      <section className="py-10 md:py-14 px-4 md:px-8 bg-background border-y border-border/40">
        <div className="max-w-3xl mx-auto text-center reveal-on-scroll">
          <h2 className="text-2xl md:text-3xl font-semibold text-primary mb-6">
            Tu peux prendre ce temps pour toi
          </h2>
          <div className="space-y-3 text-lg text-foreground/90 leading-relaxed">
            <p>Il n'y a rien à choisir tout de suite.</p>
            <p>Tu peux simplement venir déposer ce que tu vis,</p>
            <p>et voir ce qui est juste pour toi.</p>
          </div>
        </div>
      </section>

      {/* SECTION 2 — IDENTITÉ */}
      <section id="je-suis-meredith-meneora" className="section-padding bg-accent/10">
        <div className="max-w-5xl mx-auto reveal-on-scroll">
          <div className="grid md:grid-cols-[auto,1fr] gap-10 items-start">
            <div className="flex justify-center md:pt-2">
              <img 
                src="/lovable-uploads/72665b79-799a-4fe6-a0d6-6e41baf738ec.png" 
                alt="Portrait de Meredith"
                className="w-48 h-48 md:w-56 md:h-56 object-cover rounded-full shadow-md"
              />
            </div>
            <div className="text-center md:text-left">
              <h2 className="text-3xl md:text-4xl font-bold text-primary mb-5">Je suis Meredith</h2>
              <div className="space-y-3 text-base md:text-lg text-foreground/90 leading-relaxed">
                <p>J'accompagne les personnes qui sentent que quelque chose dans leur vie demande à bouger, mais qui n'arrivent pas toujours encore à nommer quoi.</p>
                <p>Parfois cela ressemble à du flou. Parfois à une fatigue intérieure. Parfois à des blocages qui reviennent, des choix qui se répètent, une sensation d'être à côté de soi.</p>
                <p>Je connais ces endroits-là profondément, et c'est aussi pour cela que j'accompagne aujourd'hui.</p>
                <p>Mon approche mêle coaching, hypnose et travail énergétique, mais au fond, ce que j'offre d'abord, c'est un espace.</p>
                <p>Un espace où déposer ce qui pèse. Comprendre ce qui se joue. Relâcher ce qui entrave. Et retrouver ce qui, en toi, sait déjà.</p>
                <p>Je crois profondément que nous portons souvent des réponses en nous, même lorsqu'elles semblent recouvertes. Mon rôle n'est pas de te dire qui être, mais de t'aider à retrouver ce qui sonne juste pour toi.</p>
                <p>J'aime accompagner avec profondeur, simplicité, présence. Sans masque. Sans performance.</p>
                <p className="font-medium">Si tu es ici, c'est peut-être qu'une part de toi sait déjà qu'il est temps de t'écouter autrement. Et peut-être que cela peut commencer ici.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — PARCOURS */}
      <section className="section-padding bg-background">
        <div className="max-w-5xl mx-auto reveal-on-scroll">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-3">
              Tu n'as pas besoin de savoir exactement ce dont tu as besoin pour commencer.
            </h2>
            <p className="text-lg text-foreground/80">
              Deux chemins pour commencer, selon ce qui t'appelle aujourd'hui.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-accent/10 p-8 rounded-lg flex flex-col">
              <h3 className="text-2xl font-semibold text-primary mb-4">Séance sur mesure</h3>
              <div className="space-y-3 text-base text-foreground/90 mb-6 flex-grow leading-relaxed">
                <p>Tu sens que quelque chose bloque, mais tu n'arrives pas vraiment à saisir quoi.</p>
                <p>Peut-être une fatigue intérieure, un mal-être diffus, des schémas qui se répètent, une sensation d'être perdu ou à côté de toi.</p>
                <p>Parfois une seule séance peut déjà remettre de la clarté, délier quelque chose, rouvrir un espace.</p>
              </div>
              <Link to="/particulier">
                <Button variant="link" className="px-0 text-primary">
                  En savoir plus →
                </Button>
              </Link>
            </div>

            <div className="bg-primary/10 p-8 rounded-lg flex flex-col">
              <h3 className="text-2xl font-semibold text-primary mb-4">Accompagnement en profondeur</h3>
              <div className="space-y-3 text-base text-foreground/90 mb-6 flex-grow leading-relaxed">
                <p>Si tu sens que ce malaise est plus ancien, plus global… comme si quelque chose dans ta vie demandait à être revisité en profondeur.</p>
                <p>Tu n'as pas forcément besoin de savoir ce qui cloche. Juste sentir qu'une part de toi appelle un vrai changement.</p>
                <p>Cet accompagnement est là pour faire le point, mettre des mots, retrouver du sens et une direction plus juste.</p>
              </div>
              <Link to="/professionnel">
                <Button variant="link" className="px-0 text-primary">
                  Découvrir le parcours →
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 4 — SIGNATURE */}
      <section className="section-padding bg-accent/10">
        <div className="max-w-3xl mx-auto text-center reveal-on-scroll">
          <p className="font-serif italic text-2xl md:text-3xl lg:text-4xl font-light text-primary leading-relaxed tracking-wide">
            « Quand quelque chose en toi demande à évoluer, il existe un espace pour l'écouter. »
          </p>
          <p className="mt-8 text-sm md:text-base text-foreground/60 tracking-[0.2em] uppercase">
            Coaching · Hypnose · Bilan de compétences
          </p>
        </div>
      </section>

      {/* SECTION 5 — RASSURANCE + CTA FINAL */}
      <section className="section-padding bg-background">
        <div className="max-w-3xl mx-auto text-center reveal-on-scroll">
          <div className="space-y-4 text-xl text-foreground/90 mb-10 leading-relaxed">
            <p>Tu n'as pas besoin d'aller mal pour venir.</p>
            <p>Tu n'as pas besoin d'être prêt non plus.</p>
            <p className="font-medium">Juste ressentir que c'est le moment de faire le point.</p>
          </div>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
            <Button size="lg" className="text-base md:text-lg">
              Faire le point ensemble
            </Button>
          </a>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" ref={contactRef} className="section-padding bg-accent/10">
        <div className="max-w-3xl mx-auto text-center reveal-on-scroll">
          <h2 className="text-3xl font-bold text-primary mb-6">Contact</h2>
          <p className="text-lg mb-4">📱 06 07 50 74 12 — uniquement joignable via WhatsApp</p>
          <p className="text-base mb-8 text-foreground/80">
            N'hésite pas à m'écrire. Je te répondrai dès que possible.
          </p>
          <div className="flex justify-center">
            <a href="https://wa.me/33607507412" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="lg">Écrire sur WhatsApp</Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
      <ScrollToTop />
    </div>
  );
};

export default Home;
